import { createStyles, Grid, TextField, Theme, Typography, WithStyles, withStyles } from '@material-ui/core'
import moment from 'moment'
import * as React from 'react'

import OutcomeDateItem from '../CustomOutcomeDate/OutcomeDateItem'
import OutcomeDropDownItem from '../CustomOutcomeDropDown/OutcomeDropDownItem'
import {
  CareerExplorationItems,
  IncentiveEarned,
  IncentiveType,
  StipendFrequency,
  WageFrequency
} from './../../../services/Model/Outcome'
import { Nullable } from './../../../services/utils'
import IncentiveEarnedSection from './../../Outcomes/FormItems/IncentiveEarnedSection'

interface ActivityInformationProps extends WithStyles<typeof styles> {
  typeOfCareerExploration: Nullable<string>
  onTypeOfCareerExplorationChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onDateChange: (prop: string, value: Date | string) => void
  startDate: Nullable<Date>
  endDate: Nullable<Date>
  totalParticipantsCompleted: Nullable<number>
  totalParticipantsEnrolled: Nullable<number>
  onTotalParticipantsCompletedChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  totalHours: Nullable<number>,
  onTotalHoursChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  incentiveEarned: Nullable<IncentiveEarned>
  onIncentiveEarnedChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  incentiveType: Nullable<IncentiveType>
  handleIncentiveTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  stipendAmount: Nullable<number>
  stipendFrequency: Nullable<StipendFrequency>
  handleStipendAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleStipendFrequencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  wageAmount: Nullable<number>
  wageFrequency: Nullable<WageFrequency>
  handleWageAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFrequencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  minEndDate: Nullable<string | Date>
}

const ActivityInformation: React.FC<ActivityInformationProps> = (props: ActivityInformationProps) => {
  const {
    classes,
    typeOfCareerExploration,
    onTypeOfCareerExplorationChange,
    endDate,
    onDateChange,
    startDate,
    totalParticipantsCompleted,
    onTotalParticipantsCompletedChange,
    totalHours,
    onTotalHoursChange,
    incentiveEarned,
    onIncentiveEarnedChange,
    incentiveType,
    handleIncentiveTypeChange,
    stipendAmount,
    stipendFrequency,
    handleStipendAmountChange,
    handleStipendFrequencyChange,
    wageAmount,
    wageFrequency,
    handleWageAmountChange,
    onFrequencyChange,
    minEndDate,
    totalParticipantsEnrolled
  } = props
  const careerExplorationItem = CareerExplorationItems.find(i => i.value === typeOfCareerExploration)
  const [maxEndDate, setMaxEndDate] = React.useState('')

  // This is needed to not gray out dates due to setting max-date
  React.useEffect(() => {
    if (props.endDate) setMaxEndDate('')
  }, [props.endDate])

  React.useEffect(() => {
    if (props.startDate) setMaxEndDate(moment().format('YYYY-MM-DD').toString())
  }, [props.startDate]);


  return (
    <section className={classes.root}>
      <Grid container={true} spacing={8} alignItems="center">
        <Grid item={true} xs={12}>
          <Typography variant="h5" className={classes.sectionSubtitle}>
            Activity Information
          </Typography>
        </Grid>
        <Grid item={true} xs={12}>
          <OutcomeDropDownItem
            label="Type of Career Exploration Activity"
            prop="typeOfCareerExploration"
            value={careerExplorationItem ? careerExplorationItem.label : ''}
            onInputChange={onTypeOfCareerExplorationChange}
            items={CareerExplorationItems.map(i => i.label)}
            required={true}
            hideUnknown={true}
          />
        </Grid>
        <Grid item={true} xs={12}>
          <OutcomeDateItem
            label="Participant Start Date"
            prop="startDate"
            value={startDate ? moment(moment(startDate).format("YYYY-MM-DD")).toDate() : ''}
            onDateChange={({ target }) => onDateChange('startDate', target.value)}
            minDate={
              moment().subtract(100, 'years').format('YYYY-MM-DD')
            }
            maxDate={
              moment().format('YYYY-MM-DD')
            }
            required={!startDate}
          />
        </Grid>
        <Grid item={true} xs={12}>
          <OutcomeDateItem
            label="Participant End Date"
            prop="endDate"
            value={endDate ? moment(moment(endDate).format("YYYY-MM-DD")).toDate() : ''}
            onDateChange={({ target }) => onDateChange('endDate', target.value)}
            minDate={minEndDate ? minEndDate.toString() : ''}
            maxDate={maxEndDate || ''}
            required={!endDate}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={3} className={classes.label}>Total Participants Completed&#58;</Grid>
        <Grid item={true} xs={12} sm={6} md={9}>
          <TextField
            required={true}
            value={totalParticipantsCompleted || ''}
            id={"totalParticipantsCompleted"}
            className={classes.textField}
            placeholder={"0"}
            margin="normal"
            variant="outlined"
            InputProps={{
              inputProps: {
                title: `Please Provide A Valid number.`,
                className: classes.input,
                name: "totalParticipantsCompleted",
                type: "number",
                min: '0',
                max: totalParticipantsEnrolled || ''
              }
            }}
            onChange={onTotalParticipantsCompletedChange}
          />
        </Grid>

        <Grid item={true} xs={12} sm={6} md={3} className={classes.label}>Total Hours&#58;</Grid>
        <Grid item={true} xs={12} sm={6} md={9}>
          <TextField
            required={true}
            value={totalHours || ''}
            id={"totalHours"}
            className={classes.totalHours}
            placeholder={"0"}
            margin="normal"
            variant="outlined"
            InputProps={{
              inputProps: {
                title: `Please Provide A Valid number.`,
                className: classes.input,
                name: "totalHours",
                type: "number",
                min: '0'
              }
            }}
            onChange={onTotalHoursChange}
          />
        </Grid>

        <Grid item={true} xs={12}>
          <IncentiveEarnedSection
            incentiveEarned={incentiveEarned}
            onIncentiveEarnedChange={onIncentiveEarnedChange}
            incentiveType={incentiveType}
            onIncentiveTypeChange={handleIncentiveTypeChange}
            stipendAmount={stipendAmount}
            stipendFrequency={stipendFrequency}
            onStipendAmountChange={handleStipendAmountChange}
            onStipendFrequencyChange={handleStipendFrequencyChange}
            wageAmount={wageAmount}
            wageFrequency={wageFrequency}
            onWageAmountChange={handleWageAmountChange}
            onWageFrequencyChange={onFrequencyChange}
          />
        </Grid>
      </Grid>
    </section>
  )
}

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    textField: {
      minWidth: "35%",
      [theme.breakpoints.down('sm')]: {
        minWidth: "50%",
      },
    },
    input: {},
    label: {
      justifyContent: 'flex-end',
      display: 'flex',
      paddingRight: '8px',
      alignItems: 'center',

      [theme.breakpoints.down('sm')]: {
        flexBasis: '100%',
        justifyContent: 'flex-start',
      },
    },
    sectionSubtitle: {},
    totalHours: {}
  })

export default withStyles(styles)(ActivityInformation)
